import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { SearchableSelect } from '../../components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, Receipt, Trash2, Send, Edit, Eye, X, AlertCircle,
  Clock, CheckCircle, XCircle, Banknote, FileText, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  paid: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  returned: 'bg-orange-100 text-orange-800 border-orange-200',
};

const PRIORITY_COLORS = {
  Low: 'bg-gray-100 text-gray-700',
  Normal: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700',
};

const CATEGORIES = [
  { value: 'Travel', label: 'Travel' },
  { value: 'Meals & Entertainment', label: 'Meals & Entertainment' },
  { value: 'Office Supplies', label: 'Office Supplies' },
  { value: 'Equipment', label: 'Equipment' },
  { value: 'Software & Subscriptions', label: 'Software & Subscriptions' },
  { value: 'Training & Development', label: 'Training & Development' },
  { value: 'Communication', label: 'Communication' },
  { value: 'Transport', label: 'Transport' },
  { value: 'Accommodation', label: 'Accommodation' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Utilities', label: 'Utilities' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Administrative', label: 'Administrative' },
  { value: 'Miscellaneous', label: 'Miscellaneous' },
];

const MyExpenses = () => {
  const { getAuthHeaders, currentOffice } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null); // Track expense being edited
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    purpose: '',
    category: 'Miscellaneous',
    priority: 'Normal',
    currency: currentOffice?.currency || 'USD',
    department: '',
    items: [],
    // Recurring settings
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_start_date: '',
    recurring_end_date: '',
    recurring_day: 1,
  });
  
  // New item state
  const [newItem, setNewItem] = useState({
    description: '',
    amount: '',
    quantity: 1,
    category: 'Miscellaneous',
    date_incurred: '',
    notes: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchExpenses = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('status', activeTab);
      
      const response = await fetch(`${API_URL}/api/expenses/my-requests?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
        setSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [activeTab]);

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
  }, []);

  const fetchDepartments = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      // Use the /list endpoint which extracts departments from employees
      const response = await fetch(`${API_URL}/api/employees/departments/list`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      // Unified endpoint: returns office categories first, then global, then defaults
      const response = await fetch(`${API_URL}/api/expenses/categories/list`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories.map(c => ({ value: c.value || c.name, label: c.label || c.name })));
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      purpose: '',
      category: 'Miscellaneous',
      priority: 'Normal',
      currency: currentOffice?.currency || 'USD',
      department: '',
      items: [],
      // Recurring settings
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_start_date: '',
      recurring_end_date: '',
      recurring_day: 1,
    });
    setNewItem({
      description: '',
      amount: '',
      quantity: 1,
      category: 'Miscellaneous',
      date_incurred: '',
      notes: '',
    });
    setEditingExpense(null);
  };

  // Load expense data into form for editing
  const loadExpenseForEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title || '',
      description: expense.description || '',
      purpose: expense.purpose || '',
      category: expense.category || 'Miscellaneous',
      priority: expense.priority || 'Normal',
      currency: expense.currency || currentOffice?.currency || 'USD',
      department: expense.employee_department || '',
      items: (expense.items || []).map(item => ({
        ...item,
        id: item.id || Date.now().toString(),
      })),
      // Recurring settings
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || 'monthly',
      recurring_start_date: expense.recurring_start_date || '',
      recurring_end_date: expense.recurring_end_date || '',
      recurring_day: expense.recurring_day || 1,
    });
    setShowCreateModal(true);
  };

  const addItem = () => {
    if (!newItem.description || !newItem.amount) {
      toast.error('Please fill in item description and amount');
      return;
    }
    
    const item = {
      ...newItem,
      id: Date.now().toString(),
      amount: parseFloat(newItem.amount),
      quantity: parseInt(newItem.quantity) || 1,
      total: parseFloat(newItem.amount) * (parseInt(newItem.quantity) || 1),
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item],
    }));
    
    setNewItem({
      description: '',
      amount: '',
      quantity: 1,
      category: formData.category,
      date_incurred: '',
      notes: '',
    });
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleCreateExpense = async (submit = false) => {
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }
    
    if (submit && formData.items.length === 0) {
      toast.error('Add at least one expense item before submitting');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      let response;
      
      if (editingExpense) {
        // UPDATE existing expense (for drafts and returned expenses)
        response = await fetch(`${API_URL}/api/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            ...formData,
          }),
        });
        
        if (response.ok) {
          // If submit flag is true, also submit the expense after updating
          if (submit) {
            const submitResponse = await fetch(`${API_URL}/api/expenses/${editingExpense.id}/submit`, {
              method: 'POST',
              headers,
            });
            if (submitResponse.ok) {
              toast.success('Expense request updated and re-submitted for approval');
            } else {
              const error = await submitResponse.json();
              toast.error(error.detail || 'Updated but failed to submit');
            }
          } else {
            toast.success('Expense request updated');
          }
          setShowCreateModal(false);
          resetForm();
          fetchExpenses();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to update expense request');
        }
      } else {
        // CREATE new expense
        response = await fetch(`${API_URL}/api/expenses`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ...formData,
            submit,
          }),
        });
        
        if (response.ok) {
          toast.success(submit ? 'Expense request submitted' : 'Expense request saved as draft');
          setShowCreateModal(false);
          resetForm();
          fetchExpenses();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create expense request');
        }
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Failed to save expense request');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (expenseId) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/submit`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request submitted for approval');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit expense request');
      }
    } catch (error) {
      toast.error('Failed to submit expense request');
    }
  };

  const handleCancel = async (expenseId) => {
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/cancel`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request cancelled');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel expense request');
      }
    } catch (error) {
      toast.error('Failed to cancel expense request');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense request?')) return;
    
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request deleted');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete expense request');
      }
    } catch (error) {
      toast.error('Failed to delete expense request');
    }
  };

  const openDetail = (expense) => {
    setSelectedExpense(expense);
    setShowDetailModal(true);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Expenses</h1>
          <p className="text-gray-500 mt-1">Submit and track your expense requests</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-expense-button">
          <Plus className="h-4 w-4 mr-2" />
          New Expense Request
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary.total_requests || 0}</p>
                <p className="text-xs text-gray-500">Total Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-300 rounded-lg">
                <Edit className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700">{summary.draft || 0}</p>
                <p className="text-xs text-gray-500">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-200 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{summary.pending || 0}</p>
                <p className="text-xs text-yellow-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{summary.approved || 0}</p>
                <p className="text-xs text-green-600">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-200 rounded-lg">
                <XCircle className="h-5 w-5 text-red-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{summary.rejected || 0}</p>
                <p className="text-xs text-red-600">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No expense requests</h3>
              <p className="text-gray-500 mt-1">Create your first expense request to get started</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                New Expense Request
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} className="cursor-pointer hover:bg-gray-50" onClick={() => openDetail(expense)}>
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600">{expense.request_id || '-'}</span>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expense.title}
                        {expense.is_recurring && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded" title={`Recurring ${expense.recurring_frequency}`}>
                            <RefreshCw className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{expense.category}</TableCell>
                    <TableCell className="font-semibold">
                      {expense.currency} {formatCurrency(expense.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={PRIORITY_COLORS[expense.priority]}>{expense.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge className={STATUS_COLORS[expense.status] || STATUS_COLORS.pending}>
                          {expense.status === 'returned' ? 'Returned' : expense.status}
                        </Badge>
                        {expense.status === 'returned' && (
                          <span className="text-orange-500" title="Needs revision and re-submission">
                            <AlertCircle className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(expense.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(expense.status === 'draft' || expense.status === 'returned') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); loadExpenseForEdit(expense); }}
                            title="Edit"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(expense); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Expense Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateModal(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {editingExpense ? 'Edit Expense Request' : 'New Expense Request'}
            </DialogTitle>
            <DialogDescription>
              {editingExpense 
                ? `Editing ${editingExpense.request_id || 'expense'} - Update details and ${editingExpense.status === 'returned' ? 're-submit' : 'save'}`
                : 'Fill in the details and add expense items'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Title *</Label>
                <Input
                  placeholder="e.g., Business Trip to Dar es Salaam"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="expense-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <SearchableSelect
                  options={categories}
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                  placeholder="Select category"
                  searchPlaceholder="Search categories..."
                  data-testid="expense-category-select"
                />
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <SearchableSelect
                  options={departments.map(d => ({ value: d.name, label: d.name }))}
                  value={formData.department}
                  onValueChange={(v) => setFormData({ ...formData, department: v })}
                  placeholder="Select department"
                  searchPlaceholder="Search departments..."
                  emptyText="No departments found"
                  data-testid="expense-department-select"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger data-testid="expense-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Business Purpose / Justification</Label>
                <Textarea
                  placeholder="Explain the business reason for this expense..."
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Recurring Expense Settings */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="is_recurring" className="text-sm font-medium text-blue-900 cursor-pointer">
                  Make this a recurring expense
                </Label>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  Auto-creates new requests on schedule
                </span>
              </div>
              
              {formData.is_recurring && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-blue-200">
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">Frequency *</Label>
                    <Select 
                      value={formData.recurring_frequency} 
                      onValueChange={(v) => setFormData({ ...formData, recurring_frequency: v })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">
                      {formData.recurring_frequency === 'weekly' || formData.recurring_frequency === 'biweekly' 
                        ? 'Day of Week' 
                        : 'Day of Month'}
                    </Label>
                    <Select 
                      value={String(formData.recurring_day)} 
                      onValueChange={(v) => setFormData({ ...formData, recurring_day: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.recurring_frequency === 'weekly' || formData.recurring_frequency === 'biweekly' ? (
                          <>
                            <SelectItem value="1">Monday</SelectItem>
                            <SelectItem value="2">Tuesday</SelectItem>
                            <SelectItem value="3">Wednesday</SelectItem>
                            <SelectItem value="4">Thursday</SelectItem>
                            <SelectItem value="5">Friday</SelectItem>
                            <SelectItem value="6">Saturday</SelectItem>
                            <SelectItem value="7">Sunday</SelectItem>
                          </>
                        ) : (
                          Array.from({ length: 28 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.recurring_start_date}
                      onChange={(e) => setFormData({ ...formData, recurring_start_date: e.target.value })}
                      className="bg-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={formData.recurring_end_date}
                      onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                      className="bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Expense Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Expense Items</Label>
                <Badge variant="outline" className="text-lg px-3 py-1">
                  Total: {formData.currency} {formatCurrency(calculateTotal())}
                </Badge>
              </div>
              
              {/* Add Item Form */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">Description *</Label>
                    <Input
                      placeholder="Item description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      data-testid="item-description-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Amount *</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newItem.amount}
                      onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                      data-testid="item-amount-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" onClick={addItem} className="w-full" data-testid="add-item-button">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date Incurred</Label>
                    <Input
                      type="date"
                      value={newItem.date_incurred}
                      onChange={(e) => setNewItem({ ...newItem, date_incurred: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      placeholder="Optional notes"
                      value={newItem.notes}
                      onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Items List */}
              {formData.items.length > 0 && (
                <ScrollArea className="h-48 border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.description}</p>
                              {item.date_incurred && (
                                <p className="text-xs text-gray-500">{item.date_incurred}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(item.total)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateModal(false); }}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleCreateExpense(false)} disabled={saving}>
              {editingExpense ? 'Save Changes' : 'Save as Draft'}
            </Button>
            <Button onClick={() => handleCreateExpense(true)} disabled={saving || formData.items.length === 0}>
              <Send className="h-4 w-4 mr-2" />
              {editingExpense?.status === 'returned' ? 'Re-submit for Approval' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          {selectedExpense && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{selectedExpense.title}</DialogTitle>
                  <Badge className={STATUS_COLORS[selectedExpense.status]}>{selectedExpense.status}</Badge>
                </div>
                <DialogDescription className="flex items-center gap-2">
                  {selectedExpense.request_id && (
                    <span className="font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{selectedExpense.request_id}</span>
                  )}
                  {selectedExpense.request_id && <span>•</span>}
                  <span>{selectedExpense.purpose || 'No description provided'}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium">{selectedExpense.category}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Priority</p>
                    <Badge className={PRIORITY_COLORS[selectedExpense.priority]}>{selectedExpense.priority}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedExpense.currency} {formatCurrency(selectedExpense.total_amount)}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <Label className="text-sm font-semibold">Expense Items</Label>
                  <ScrollArea className="h-48 mt-2 border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Qty</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedExpense.items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>

                {/* Status Info */}
                {selectedExpense.status === 'rejected' && selectedExpense.rejection_reason && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{selectedExpense.rejection_reason}</p>
                    <p className="text-xs text-red-500 mt-1">
                      Rejected by {selectedExpense.rejected_by_name} on {new Date(selectedExpense.rejected_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Returned/Sent Back Info */}
                {selectedExpense.status === 'returned' && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm font-medium text-orange-800 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Request Returned for Revision
                    </p>
                    {selectedExpense.return_comment && (
                      <p className="text-sm text-orange-700 mt-2 bg-white p-2 rounded border border-orange-200">
                        "{selectedExpense.return_comment}"
                      </p>
                    )}
                    <p className="text-xs text-orange-500 mt-2">
                      Returned by {selectedExpense.returned_by_name} on {new Date(selectedExpense.returned_at).toLocaleDateString()}
                      {selectedExpense.returned_at_stage && ` (at ${selectedExpense.returned_at_stage} stage)`}
                    </p>
                    <p className="text-xs text-orange-600 mt-1 font-medium">
                      Please review the feedback, make necessary changes, and re-submit.
                    </p>
                  </div>
                )}

                {selectedExpense.status === 'approved' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">Approved</p>
                    {selectedExpense.approval_notes && <p className="text-sm text-green-700">{selectedExpense.approval_notes}</p>}
                    <p className="text-xs text-green-500 mt-1">
                      Approved by {selectedExpense.approved_by_name} on {new Date(selectedExpense.approved_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex gap-2">
                {selectedExpense.can_cancel && (
                  <Button variant="outline" onClick={() => handleCancel(selectedExpense.id)}>
                    Cancel Request
                  </Button>
                )}
                {(selectedExpense.status === 'draft' || selectedExpense.status === 'returned') && (
                  <Button variant="destructive" onClick={() => handleDelete(selectedExpense.id)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                )}
                {selectedExpense.can_submit && (
                  <Button onClick={() => handleSubmit(selectedExpense.id)}>
                    <Send className="h-4 w-4 mr-2" />
                    {selectedExpense.status === 'returned' ? 'Re-submit for Approval' : 'Submit for Approval'}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default MyExpenses;
